import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  AddStationFormFieldsEnum,
  DirectionEnum,
  editFormDataAF,
  EditFormMode,
  errorsCrudContentAtomFamily,
  getIsValidStationLocation,
  useDrawers,
  type IPassengersMapLocationInfo,
  type IStationDto,
} from "@ryde/shared";

import { yupAddStationValidationSchema } from "../validations";
import { usePassengerMapSelectedControl } from "./use-passenger-map-selected-control";
import { usePassengerMapStations } from "./use-passenger-map-stations";

export const usePassengerMapAddStationPanel = () => {
  const { openEditFormDrawer } = useDrawers();
  const { showAddedStation } = usePassengerMapStations();
  const { resetSelectedControl } = usePassengerMapSelectedControl();
  const setServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily("station"),
  );

  const setStationDto = useSetRecoilState(
    editFormDataAF(["station", EditFormMode.UnboundDrawer]),
  );
  const resolver = useYupValidationResolver(
    yupAddStationValidationSchema,
    "passenger",
  );

  const methods = useForm<{
    address?: IPassengersMapLocationInfo;
  }>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      address: undefined,
    },
    resolver,
  });

  const {
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const addressesValue = watch(AddStationFormFieldsEnum.Addresses);

  const isDisabled = !isValid || !getIsValidStationLocation(addressesValue);

  const handleClick = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }
    setServerErrors(undefined);

    const addresses = getValues(AddStationFormFieldsEnum.Addresses);

    const initStationData: Partial<IStationDto> = {
      name: addresses?.fullAddress,
      direction: DirectionEnum.Both,
      metadata: {
        isActive: true,
        isOfficial: true,
        locationInfo: addresses,
      },
    };

    setStationDto(initStationData);
    const simpleResult = await openEditFormDrawer({
      id: undefined,
      entity: "station",
      onCloseAction() {
        setStationDto(undefined);
      },
    });

    const { success, data } = simpleResult ?? {};

    if (success && data?.id) {
      resetSelectedControl();
      showAddedStation(data as IStationDto);
    }
  });

  return {
    methods,
    handleClick,
    isDisabled,
  };
};
